// eslint-disable-next-line
// @ts-nocheck
import { bind } from 'hyperhtml';
import {
	contentTabId,
	getSidebarData,
	updateLockedStatus,
	getFavoriteItemTypes
} from '../NavigationPanel/sidebarDataConverter';
import HeaderTabsContainer from './HeaderTabsContainer';
import {
	cuiBackgroundPage,
	CuiLayout,
	cuiMethods,
	cuiToc,
	cuiToolbar
} from 'cui';
import mainWindowActions from './MainWindowCuiActions';
import { itemType as itemTypeMetadata } from 'metadata';
import { favorites } from 'store';
import type { Favorite } from '../store/storeTypes';
import type { HeaderTab } from '../aras/startup/headerTabs';
import HeaderTabs from '../aras/startup/headerTabs';
import { openStartItems } from '../aras/startup/openStartItems';
import type NavigationPanel from '../NavigationPanel/NavigationPanel';

interface MainWindowLayoutState {
	splitScreen: {
		dockTabs: Array<string>;
		mainTabs: Array<string>;
		tabs: Record<string, HeaderTab>;
		selectedTabs: Array<string>;
	};
	navigationPanel: {
		isVisible: boolean;
	};
}

interface MainWindowLayoutProps {
	favorites: Record<Favorite['id'], Favorite>;
}

export default class MainWindowCuiLayout extends CuiLayout<
	MainWindowLayoutState,
	MainWindowLayoutProps
> {
	_initBackgroundPromise = null;
	_initTocPromise = null;
	backgroundPage = document.getElementById('backgroundPage');
	navigationPanel: NavigationPanel = document.getElementById('navigationPanel');
	tabsContainer: null | HeaderTabsContainer = null;
	actions = mainWindowActions(this.setState.bind(this));
	initialState: MainWindowLayoutState = {
		splitScreen: {
			dockTabs: [],
			mainTabs: [],
			tabs: {},
			selectedTabs: []
		},
		navigationPanel: {
			isVisible: false
		}
	};
	#toolbarComponent = document.getElementById('headerCommandsBar');
	definedControls = new Map([
		['MainView.Header', this.#toolbarComponent],
		['MainView.PopupMenu', this.navigationPanel.popupMenu],
		['MainView.PopupMenuSecondaryMenu', this.navigationPanel.secondaryPopupMenu]
	]);

	async init() {
		const waitForInit = super.init();

		const dockTabs = document.getElementById('dock-tab');
		const mainTabs = document.getElementById('main-tab');
		if (dockTabs instanceof HeaderTabs && mainTabs instanceof HeaderTabs) {
			this.tabsContainer = new HeaderTabsContainer(dockTabs, mainTabs, {
				actions: this.actions
			});
		}

		sessionStorage.setItem('defaultDState', 'defaultDState');

		const commandFactory = cuiMethods.getLayoutCommandFactory(this.options);
		this.options.commandFactory = commandFactory;
		const initBackgroundPage = this._initializeBackgroundPage();
		const initToc = this._initializeToc();

		await initToc;
		openStartItems(this);
		this._registerTocHandlers();

		this._registerNavPanelHandlers();
		this._setupNavPanel();

		this._registerSidebarHandlers();
		await this._initializeSidebar();

		await initBackgroundPage;
		this._registerBackgroundPageHandlers();
		await waitForInit;

		this._initializeHeader();
		this._registerHeaderHandlers();

		this._registerHeaderTabsContextMenuHandlers(
			this.cuiControls.get('MainView.HeaderTabsContextMenu')
		);
	}

	updateLayout(oldProps, oldState) {
		this.navigationPanel.setState({ favorites: this.props.favorites });
		this.renderHeader(oldState);
		this._updateBackgroundPage(oldState);
		this.#updateSideBar();
	}

	#updateSideBar() {
		const tabs = this.navigationPanel.tabs;
		const navData = this.navigationPanel.nav.data;
		const favoriteItemTypes = getFavoriteItemTypes(this.props.favorites);
		const sidebarData = getSidebarData(favoriteItemTypes, navData);
		tabs.setState({ favorites: sidebarData });
	}

	renderHeader(oldState) {
		if (oldState.navigationPanel !== this.state.navigationPanel) {
			this.observer.notify('NavigationPanelVisibility');
		}
	}

	async updateCuiLayoutOnItemChange(itemTypeName) {
		const itemType = await itemTypeMetadata.getItemType(
			'CuiDependency',
			'name'
		);
		const morphaes = itemType['Morphae'] ?? [];
		const needToCuiUpgrade = morphaes.some((morphae) => {
			const morphaeName = morphae['related_id@aras.name'];
			return morphaeName === itemTypeName;
		});
		if (needToCuiUpgrade) {
			this.observer.notify('reInitByCUIDependencies');
		}
	}

	mapStateToProps({ favorites }) {
		const filteredFavorites = Object.values(favorites).reduce(
			(acum, favoriteItem) => {
				const { category } = favoriteItem;
				if (['ItemType', 'Item', 'Search'].includes(category)) {
					acum[favoriteItem.id] = favoriteItem;
				}
				return acum;
			},
			{}
		);
		return { favorites: filteredFavorites };
	}

	async _initializeBackgroundPage(options = {}) {
		if (this._initBackgroundPromise) {
			return this._initBackgroundPromise;
		}

		this._initBackgroundPromise = cuiBackgroundPage(
			this.backgroundPage,
			'BackgroundPage',
			{ ...this.options, ...options }
		);
		await this._initBackgroundPromise;
		this._initBackgroundPromise = null;
	}

	_registerHeaderTabsContextMenuHandlers(contextMenu) {
		this.tabsContainer.attachContextMenu(contextMenu);
	}

	async _initializeHeader() {
		this.#toolbarComponent.firstChild.classList.add('aras-header');

		this.options.toggleSplitScreenMode = () =>
			this.tabsContainer.toggleSplitScreenMode();
	}

	_initializeSidebar() {
		this.#updateSideBar();
		const tabs = this.navigationPanel.tabs;
		return tabs.selectTab(contentTabId);
	}

	async _initializeToc() {
		if (this._initTocPromise) {
			return this._initTocPromise;
		}

		const nav = this.navigationPanel.nav;
		this._initTocPromise = cuiToc(nav, 'TOC', this.options);

		await this._initTocPromise;
		this._initTocPromise = null;

		return this._initTocPromise;
	}

	_setupNavPanel() {
		const isTocPinned =
			aras.getPreferenceItemProperty(
				'Core_GlobalLayout',
				null,
				'core_toc_pinned'
			) === '1';

		if (isTocPinned) {
			this.navigationPanel.togglePin(true);
			this.navigationPanel.toggleVisibility(true);
		}
	}

	_registerBackgroundPageHandlers() {
		this.observer.subscribe(async (event) => {
			if (event !== 'UpdateTOC') {
				return;
			}

			await this._initializeBackgroundPage();
			this._toggleBackgroundPage(this.state.splitScreen);
		});
	}

	_registerNavPanelHandlers() {
		this.navigationPanel.addEventListener(
			'navigationPanelContextMenu',
			async (event) => {
				const { x, y, controlName, itemTypeName, itemTypeId, ...detail } =
					event.detail;
				const criteria = itemTypeId
					? [itemTypeId, 'id']
					: [itemTypeName, 'name'];
				const itemTypePromise = itemTypeMetadata.getItemType(...criteria);

				const itemType = await itemTypePromise;
				const options = {
					...detail,
					favorites,
					itemType,
					itemTypeName: itemType?.name
				};
				this.cuiControls.get(controlName).show({ x, y }, options);
			}
		);
		this.navigationPanel.addEventListener('sidebarPin', (event) => {
			const { state } = event.detail;
			aras.setPreferenceItemProperties('Core_GlobalLayout', null, {
				core_toc_pinned: Number(state)
			});
		});
		this.navigationPanel.addEventListener('sidebarClose', () => {
			this.actions.navigationPanel.updateVisibility(false);
		});
		this.navigationPanel.addEventListener('sidebarShow', () => {
			this.actions.navigationPanel.updateVisibility(true);
		});
	}

	_registerHeaderHandlers() {
		this.observer.subscribe(async (event) => {
			switch (event) {
				case 'reInitByCUIDependencies':
					await cuiToolbar(
						this.#toolbarComponent,
						'MainWindowHeader',
						this.options
					);
					break;
				case 'UpdateNotifications':
				case 'UpdatePreferences':
				case 'UpdateState':
				case 'NavigationPanelVisibility':
					cuiMethods.reinitControlItems(
						this.#toolbarComponent,
						event,
						this.options
					);
					break;
			}
		});
		aras.registerEventHandler('PreferenceValueChanged', window, () => {
			this.observer.notify('UpdatePreferences');
		});
	}

	_registerSidebarHandlers() {
		this.observer.subscribe(async (event) => {
			if (event !== 'UpdateTOC') {
				return;
			}

			await this._initializeToc();
			const nav = this.navigationPanel.nav;
			const tabs = this.navigationPanel.tabs;
			updateLockedStatus(tabs.data, nav.data);
			tabs.render();
		});
	}

	_registerTocHandlers() {
		this.observer.subscribe(async (event) => {
			if (event !== 'UpdateTOC') {
				return;
			}

			aras.MetadataCache.DeleteConfigurableUiDatesFromCache();
			await this._initializeToc();
			this.navigationPanel.resetState();
		});
	}

	async _toggleBackgroundPage({ dockTabs, mainTabs }) {
		const hidden = dockTabs.length > 0 || mainTabs.length > 0;
		if (hidden) {
			await this._initBackgroundPromise;
			bind(this.backgroundPage)``;
		} else {
			await this._initializeBackgroundPage({ renderOnly: true });
		}
	}

	_updateBackgroundPage(oldState) {
		const splitScreenState = this.state.splitScreen;
		if (splitScreenState === oldState.splitScreen) {
			return;
		}

		this._toggleBackgroundPage(splitScreenState);
	}
}
